import NumberInput from '@wandb/weave/common/components/elements/NumberInput';
import {Select} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useState} from 'react';
import {SingleValue} from 'react-select';

import {useDebouncedValue} from '../../../../../util/useDebouncedValue';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsDrawerContext} from '../MediaSettingsDrawerContextProvider';

type MaxItemCountOption = {
  label: OptionLabel;
  value: number;
};

enum OptionLabel {
  SHOW_ALL = 'Show all',
  LIMIT_MEDIA = 'Limit media',
}

const MAX_ITEM_COUNT_OPTIONS: MaxItemCountOption[] = Object.values(
  OptionLabel
).map((label, index) => ({
  label,
  value: index,
}));

const DEFAULT_MAX_ITEM_COUNT = 25;

export const MaxItemCount = () => {
  const {config, updateConfig, unsetConfigField} =
    useMediaSettingsDrawerContext();

  const [selectedOption, setSelectedOption] = useState(
    config.maxGalleryItems === undefined
      ? OptionLabel.SHOW_ALL
      : OptionLabel.LIMIT_MEDIA
  );

  const updateConfigCallback = useCallback(
    (value: number | undefined) => {
      if (value === undefined) {
        unsetConfigField('maxGalleryItems');
      } else {
        updateConfig({maxGalleryItems: value});
      }
    },
    [updateConfig, unsetConfigField]
  );

  const {
    value: debouncedMaxItemCount,
    debouncedSetValue: debouncedUpdateConfig,
    instantSetValue: instantUpdateConfig,
  } = useDebouncedValue<number | undefined>(
    config.maxGalleryItems,
    500,
    updateConfigCallback
  );

  const [maxItemCount, setMaxItemCount] = useState(debouncedMaxItemCount);

  const onChangeDropdown = (option: SingleValue<MaxItemCountOption>) => {
    if (option) {
      setSelectedOption(option.label);
      if (
        option.label === OptionLabel.LIMIT_MEDIA &&
        config.maxGalleryItems == null
      ) {
        setMaxItemCount(DEFAULT_MAX_ITEM_COUNT);
        instantUpdateConfig(DEFAULT_MAX_ITEM_COUNT);
      } else if (option.label === OptionLabel.SHOW_ALL) {
        setMaxItemCount(undefined);
        instantUpdateConfig(undefined);
      }
    }
  };

  const onChangeInput = (value?: number) => {
    if (value != null) {
      setMaxItemCount(value);
      debouncedUpdateConfig(value);
    }
  };
  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Media display limit"
        configField="maxGalleryItems"
        unsetConfigField={() => instantUpdateConfig(undefined)}
      />
      <div className="flex">
        <Select<MaxItemCountOption>
          className="w-full"
          aria-label="Media display limit"
          options={MAX_ITEM_COUNT_OPTIONS}
          value={MAX_ITEM_COUNT_OPTIONS.find(
            option => option.label === selectedOption
          )}
          onChange={onChangeDropdown}
        />
        {selectedOption === OptionLabel.LIMIT_MEDIA && (
          <div className="ml-8" data-test="max-item-count-input">
            <NumberInput
              min={0}
              max={108}
              strideLength={1}
              value={maxItemCount ?? DEFAULT_MAX_ITEM_COUNT}
              onChange={onChangeInput}
              stepper
              useStepperPlusMinus
            />
          </div>
        )}
      </div>
    </div>
  );
};
